import type { SignalRow } from '@shared/flow/signal-view';
import {
  effectiveSignalStatus,
  isPausedLikeSignalStatus,
  resolveSignalVerificationState,
  stepLabelFor,
  NO_STEP_DATA_LABEL,
  type SignalVerificationState,
} from '@shared/flow/signal-verification';

/**
 * The Signals-tab row action matrix — the single source of truth for which
 * buttons a row (table row or Timeline affordance, bead .16) gets (plan
 * 2026-09-24-002 U6, bead .15), rebuilt on top of the nine-state
 * verification model (Signals tab PR 1 QA batch 2, unit B2, D-A — see
 * `@shared/flow/signal-verification.ts` for the authoritative state
 * resolver, its evaluation order, and its "Divergences from Action View"
 * section — this module renders that state, it does not re-derive it).
 * Ported/adapted from Action View's own `getRowActions`
 * (`mini-apps/action-view/row-actions.ts`) — copied per CLAUDE.md's mini-app
 * boundary rule, not imported.
 */
export type SignalRowActionsInput = Pick<
  SignalRow,
  | 'action_state'
  | 'signal_status'
  | 'signal_tactic'
  | 'gong_verified_at'
  | 'gong_probe_status'
  | 'gong_state'
>;

/** The verb button label when the row's own Gong step cannot be named (Gong reconcile U26, D9). */
export const OPEN_IN_GONG_LABEL = 'Open in Gong';

/**
 * QA batch 6 D2 (superseded design, see below) — gates the "Indefinitely"
 * option inside `SnoozePopover` (Spencer's override on qa-batch6-plan.md:
 * Done was never split into its own button; instead the row's ONE
 * "Mark as read" button always opens the popover, which lists "Indefinitely"
 * — "Comes back when Gong moves to the next step" — first, but ONLY when
 * the row carries a full Gong step signature. A row with no signature sees
 * just the four dated options; the `needs_date` recovery path is
 * unaffected). Moved here (rather than inlined in the popover, or widened
 * into a `showDone`/`showMarkAsRead` field on `SignalRowActions`, R3) so
 * `rowActions.test.ts` pins it independently of the eleven `toEqual` blocks
 * that already pin the rest of this matrix.
 */
export function hasSnoozeStepSignature(
  row: Pick<SignalRow, 'signal_instance_id' | 'step_number' | 'signal_tactic'>,
): boolean {
  return !!(row.signal_instance_id && row.step_number != null && row.signal_tactic);
}

/**
 * Badge tone for the Signal card's state chip (D-A/D-I) — the row itself keeps its own Flow status column, so this chip is not rendered a second time there.
 * `unverified` has no chip — the Signal card's Gong status line carries it (qa-batch4-plan.md D-B); the remaining chips are verdicts, not freshness.
 */
export type SignalRowActionChip = { kind: 'grey' | 'amber' | 'muted'; label: string };

/**
 * The row's primary Gong verb; `null` renders no verb. A `label` verb is a step verb ("Send Email" | "Call" | …),
 * only ever produced for `live_manual` (D-C: no fallback verb, ever). An `open_in_gong` verb is the generic
 * "Open in Gong" link (Gong reconcile U26, D9); the component resolves its href via `useGongOpenLink`, this
 * module stays pure.
 */
export type SignalRowVerb = null | { kind: 'label'; label: string } | { kind: 'open_in_gong'; label: string };

export interface SignalRowActions {
  /** The resolved verification state — exposed so a consumer can key an effect/log off it without re-deriving. */
  state: SignalVerificationState;
  /** Signal card meta-line chip; `null` renders no chip. */
  chip: SignalRowActionChip | null;
  /** Archive button (opens the reason+feedback form). */
  showArchive: boolean;
  /** Snooze button (opens the snooze-until popover). */
  showSnooze: boolean;
  /** "Bring back" (un-snooze) button. */
  showBringBack: boolean;
  /** The primary Gong verb — see `SignalRowVerb`. */
  verb: SignalRowVerb;
  /** Fields to prefill on the Archive form when it opens from this row. */
  archivePrefill: { reason?: string; feedback?: string };
}

const FLOW_STATUS_FEEDBACK: Record<'Paused' | 'Pending' | 'Finished', string> = {
  Paused: 'Flow paused in Gong',
  Pending: 'Flow pending in Gong',
  Finished: 'Flow finished in Gong',
};

const OPEN_IN_GONG_VERB: SignalRowVerb = { kind: 'open_in_gong', label: OPEN_IN_GONG_LABEL };

/**
 * The verb for an actionable row (anything but closed / snoozed / no-flow, which never carry one), with the
 * Gong reconcile U26 `gong_state` overlay folded in. A legacy row (no `gong_state`) and a `done` row get the
 * step verb, if any; `fix_in_gong` always sends the rep to Gong; `waiting` suppresses the verb; `do_it_now`
 * keeps the step verb unless `openInGongOnDoItNow` (D9: Running with no SF record — step type unknown — is
 * real work, so send the rep to Gong).
 */
function verbFor(
  row: SignalRowActionsInput,
  stepLabel: string | null,
  opts?: { openInGongOnDoItNow?: boolean },
): SignalRowVerb {
  const stepVerb: SignalRowVerb = stepLabel ? { kind: 'label', label: stepLabel } : null;
  switch (row.gong_state) {
    case null:
    case undefined:
    case 'done':
      return stepVerb;
    case 'fix_in_gong':
      return OPEN_IN_GONG_VERB;
    case 'waiting':
      return null;
    case 'do_it_now':
      return opts?.openInGongOnDoItNow ? OPEN_IN_GONG_VERB : stepVerb;
  }
}

/**
 * The `unverified` shape — also the fail-closed answer should a state/status pair ever disagree.
 * No chip (qa-batch4-plan.md D-B): the Signal card's Gong status line is the ONE rendering of
 * "not confirmed recently", so a chip here could only repeat — or contradict — it.
 */
function unverifiedActions(row: SignalRowActionsInput): SignalRowActions {
  return {
    state: 'unverified',
    chip: null,
    showArchive: true,
    showSnooze: true,
    showBringBack: false,
    verb: verbFor(row, null),
    archivePrefill: {},
  };
}

/**
 * `now` defaults to the current time TEMPORARILY — wave 1 (B2) lands before
 * its consumers (B6's table config, B7's `SignalRowActions.tsx`/Timeline)
 * are updated to pass one explicitly, and this keeps them compiling in the
 * meantime. Remove the default once every call site passes `now` (plan
 * `qa-batch2-plan.md` unit B2's gates note).
 */
export function getSignalRowActions(row: SignalRowActionsInput, now: Date = new Date()): SignalRowActions {
  const state = resolveSignalVerificationState(row, now);

  switch (state) {
    case 'closed':
    case 'no_flow':
      return {
        state,
        chip: null,
        showArchive: false,
        showSnooze: false,
        showBringBack: false,
        verb: null,
        archivePrefill: {},
      };

    case 'snoozed':
      return {
        state,
        chip: null,
        showArchive: false,
        showSnooze: false,
        showBringBack: true,
        verb: null,
        archivePrefill: {},
      };

    case 'removed':
      return {
        state,
        chip: { kind: 'grey', label: 'Removed from flow' },
        showArchive: true,
        showSnooze: false,
        showBringBack: false,
        verb: verbFor(row, null),
        archivePrefill: { reason: 'Removed from Flow', feedback: 'No longer in flow' },
      };

    case 'paused': {
      // The SAME status the resolver decided on — the probe's verdict under a
      // whole observation, else the snapshot (fix-plan-batch2.md D-A); reading
      // `signal_status` here would label a probe-Paused row "Running".
      // resolveSignalVerificationState only returns 'paused' for these three.
      // The type guard only narrows; it cannot fail for a 'paused' state.
      const status = effectiveSignalStatus(row);
      if (!isPausedLikeSignalStatus(status)) return unverifiedActions(row);
      return {
        state,
        chip: { kind: 'amber', label: status },
        showArchive: true,
        showSnooze: false,
        showBringBack: false,
        verb: verbFor(row, null),
        archivePrefill: { feedback: FLOW_STATUS_FEEDBACK[status] },
      };
    }

    case 'unverified':
      return unverifiedActions(row);

    case 'live_auto':
      return {
        state,
        chip: { kind: 'grey', label: 'Auto' },
        showArchive: true,
        showSnooze: true,
        showBringBack: false,
        verb: verbFor(row, null),
        archivePrefill: {},
      };

    case 'live_unknown_step':
      return {
        state,
        chip: { kind: 'muted', label: NO_STEP_DATA_LABEL },
        showArchive: true,
        showSnooze: true,
        showBringBack: false,
        verb: verbFor(row, null, { openInGongOnDoItNow: true }),
        archivePrefill: {},
      };

    case 'live_manual':
      return {
        state,
        chip: null,
        showArchive: true,
        showSnooze: true,
        showBringBack: false,
        verb: verbFor(row, stepLabelFor(row.signal_tactic)),
        archivePrefill: {},
      };
  }
}
